import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Crown, Check, Star, Sparkles, ArrowLeft, Loader2,
  CreditCard, Shield, Zap, Heart, Moon, Sun, Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  numerologia: Calculator,
  astrozodiac: Sun,
  mysteriae: Moon,
  reveliae_integral: Crown,
  reveliae_trial: Zap
};

const PLAN_COLORS = {
  numerologia: 'from-violet-500 to-purple-600',
  astrozodiac: 'from-amber-500 to-orange-500',
  mysteriae: 'from-indigo-500 to-purple-600',
  reveliae_integral: 'from-rose-500 via-purple-500 to-indigo-500',
  reveliae_trial: 'from-emerald-500 to-teal-500'
};

const PlanCard = ({ plan, isPopular, isTrial, onSelect, loading }) => {
  const Icon = PLAN_ICONS[plan.id] || Star;
  const gradient = PLAN_COLORS[plan.id] || 'from-gray-500 to-gray-600';
  const isIntegral = plan.is_integral && !plan.is_trial;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative bg-white rounded-3xl border-2 ${
        isTrial ? 'border-emerald-300 shadow-xl shadow-emerald-100' :
        isIntegral ? 'border-purple-300 shadow-xl shadow-purple-100' : 'border-gray-200'
      } p-6 flex flex-col`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
            POPULAIRE
          </span>
        </div>
      )}
      
      {isTrial && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 bg-gradient-to-r from-emerald-400 to-teal-500 text-white text-xs font-bold rounded-full shadow-lg">
            ESSAI 7 JOURS
          </span>
        </div>
      )}
      
      {/* Header */}
      <div className="text-center mb-6">
        <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} mb-4`}>
          <Icon className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
      </div>
      
      {/* Price */}
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-gray-900">{plan.price.toFixed(2)}€</span>
          <span className="text-gray-500">{isTrial ? '/7 jours' : '/mois'}</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">TTC • Sans engagement</p>
      </div>
      
      {/* Features */}
      <ul className="space-y-3 mb-6 flex-1">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className={`h-5 w-5 flex-shrink-0 ${
              isTrial ? 'text-emerald-500' : isIntegral ? 'text-purple-500' : 'text-green-500'
            }`} />
            <span className="text-sm text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>
      
      {/* CTA */}
      <Button
        onClick={() => onSelect(plan.id)}
        disabled={loading}
        className={`w-full py-3 rounded-xl font-semibold ${
          isTrial 
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white'
            : isIntegral 
              ? 'bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 hover:from-rose-600 hover:via-purple-600 hover:to-indigo-600 text-white'
              : `bg-gradient-to-r ${gradient} hover:opacity-90 text-white`
        }`}
        data-testid={`select-plan-${plan.id}`}
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <CreditCard className="h-5 w-5 mr-2" />
            {isTrial ? 'Essayer maintenant' : 'Souscrire'}
          </>
        )}
      </Button>
    </motion.div>
  );
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  
  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchSubscriptionStatus();
    }
  }, [isAuthenticated]);
  
  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/plans`);
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      toast.error('Erreur lors du chargement des offres');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/status`, {
        withCredentials: true
      });
      setCurrentSubscription(response.data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };
  
  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour souscrire');
      navigate('/auth', { state: { from: { pathname: '/premium' } } });
      return;
    }
    
    setSelectedPlan(planId);
    setProcessingPayment(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/premium/checkout/stripe`, {
        plan_id: planId,
        origin_url: window.location.origin,
        payment_method: 'stripe'
      }, {
        withCredentials: true
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(response.data.message || 'Erreur de paiement');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors du paiement');
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  // Separate plans
  const trialPlan = plans.find(p => p.is_trial);
  const integralPlan = plans.find(p => p.is_integral && !p.is_trial);
  const appPlans = plans.filter(p => !p.is_integral && !p.is_trial);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-indigo-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">REVELIAE</span>
          </button>
          
          {isAuthenticated && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{user?.first_name || user?.email}</span>
              {currentSubscription?.is_active && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  Premium
                </span>
              )}
            </div>
          )}
        </div>
      </header>
      
      {/* Hero */}
      <section className="py-16 px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full border border-purple-200 mb-6">
            <Crown className="h-4 w-4 text-purple-600" />
            <span className="text-purple-700 text-sm font-medium">Premium</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="text-gray-900">Débloquez tout le potentiel de</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-500 to-indigo-600">
              REVELIAE
            </span>
          </h1>
          
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Accédez à des analyses approfondies, des fonctionnalités exclusives et une expérience personnalisée.
          </p>
        </motion.div>
      </section>
      
      {/* Current subscription notice */}
      {currentSubscription?.is_active && (
        <div className="max-w-6xl mx-auto px-4 mb-8">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">Abonnement actif</p>
              <p className="text-sm text-green-600">
                {currentSubscription.has_integral 
                  ? 'REVELIAE Intégral' 
                  : `Plans actifs: ${currentSubscription.plans.join(', ')}`}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Trial Plan - First, prominently displayed */}
      {trialPlan && (
        <section className="max-w-xl mx-auto px-4 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 rounded-3xl blur-xl" />
            <PlanCard 
              plan={trialPlan} 
              isTrial={true}
              onSelect={handleSelectPlan}
              loading={processingPayment && selectedPlan === trialPlan.id}
            />
          </motion.div>
        </section>
      )}
      
      {/* Divider after trial */}
      {trialPlan && (
        <div className="max-w-4xl mx-auto px-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-gray-400 text-sm">ou abonnez-vous directement</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        </div>
      )}
      
      {/* Integral Plan (Featured) */}
      {integralPlan && (
        <section className="max-w-2xl mx-auto px-4 mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 via-purple-500/20 to-indigo-500/20 rounded-3xl blur-xl" />
            <PlanCard 
              plan={integralPlan} 
              isPopular={true}
              onSelect={handleSelectPlan}
              loading={processingPayment && selectedPlan === integralPlan.id}
            />
          </motion.div>
        </section>
      )}
      
      {/* Divider */}
      <div className="max-w-4xl mx-auto px-4 mb-12">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-gray-400 text-sm">ou choisissez par application</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
      </div>
      
      {/* Individual Plans */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {appPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <PlanCard 
                plan={plan}
                onSelect={handleSelectPlan}
                loading={processingPayment && selectedPlan === plan.id}
              />
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* Trust badges */}
      <section className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8 text-green-500" />
              <p className="font-medium text-gray-900">Paiement sécurisé</p>
              <p className="text-sm text-gray-500">Stripe & PayPal</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Zap className="h-8 w-8 text-amber-500" />
              <p className="font-medium text-gray-900">Sans engagement</p>
              <p className="text-sm text-gray-500">Résiliable à tout moment</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Heart className="h-8 w-8 text-rose-500" />
              <p className="font-medium text-gray-900">Satisfait ou remboursé</p>
              <p className="text-sm text-gray-500">14 jours pour changer d'avis</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* LUMINA Note */}
      <section className="py-8 px-4 text-center">
        <p className="text-gray-500 text-sm">
          <Sparkles className="h-4 w-4 inline mr-1" />
          <strong>LUMINA</strong> (Chiromancie) reste <strong>100% gratuit</strong> après inscription.
        </p>
      </section>
    </div>
  );
};

export default SubscriptionPage;
